import React, { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import {
    ErrorStateView,
    EmptyStateView,
} from "kontrolEdge/components/Backbone/BackboneDetails/StateViews";
import { useFwaasDetails } from "./useFwaasDetails";
import FwaasDetailsSkeleton from "./FwaasDetailsSkeleton";
import FwaasHeader from "./header/FwaasHeader";
import {
    deviceVerdict,
    needsAttention,
    readingCoverage,
    type AttentionItem,
    type AttentionNode,
} from "./attention/needsAttention";
import NeedsAttentionCard from "./attention/NeedsAttentionCard";
import DeviceHealthCard from "./health/DeviceHealthCard";
import ServiceMapCard from "./topology/ServiceMapCard";
import { useMapSelection } from "./topology/useMapSelection";
import { useFwaasOverview } from "./overview/useFwaasOverview";
import LicensesCard from "./overview/LicensesCard";
import type { FwaasOverview } from "./overview/overview.types";
import { useFwaasReading } from "./readings/useFwaasReading";
import PoliciesCard, { type RuleFlash } from "./policies/PoliciesCard";
import type { PolicyFilter } from "./policies/policies.types";
import { scrollToId } from "./detailScroll";

const ATTENTION_ANCHOR = "fwaas-needs-attention";

/**
 * Virtual Firewall detail (CTX-732), as approved in design review (CTX-1208): the
 * header; the service map, always visible, with an inspector for each part of the
 * service (CTX-1209, CTX-1264); what needs attention beside the device's health; then
 * the rules beside the licenses. Every button leads somewhere on the page (CTX-1265).
 * Each block loads on its own, so a slow FortiManager reading never holds the page.
 * Rebuilt from its URL alone (`?fwaasId=`, and `?node=` for the map), like every detail
 * view (CTX-773).
 *
 * One breakpoint for the whole view, `min-[1440px]:` (CTX-1391): from there the map's
 * inspector and the Licenses card sit beside the map and Policies; below it they stack.
 * At `xl` (1280) the map — never narrower than 760 px — lost its right lane to the
 * 348 px inspector beside it.
 */
const FwaasDetails: React.FC = () => {
    const [searchParams] = useSearchParams();
    const fwaasId = searchParams.get("fwaasId");
    const api = useAxiosAuth0();
    const [refresh, setRefresh] = useState(0);
    const { tenantId } = useAppSelector((state) => state.selection);

    const { raw, loading, error } = useFwaasDetails(fwaasId, api, refresh, tenantId ?? null);
    const overview = useFwaasOverview(fwaasId, api, refresh, tenantId ?? null);
    const scope = [fwaasId, api, refresh, tenantId ?? null] as const;
    const policies = useFwaasReading("policies", ...scope);
    const routing = useFwaasReading("routing", ...scope);
    const nat = useFwaasReading("nat", ...scope);
    const events = useFwaasReading("events", ...scope);
    // `undefined` = still on its way; `null` = the firewall could not answer it.
    const section = <K extends keyof FwaasOverview>(key: K) => (overview === null ? undefined : overview[key]);
    const [policyFilter, setPolicyFilter] = useState<PolicyFilter>("all");
    const [ruleFlash, setRuleFlash] = useState<RuleFlash | null>(null);
    // Opens on the firewall itself, its inspector beside the map, unless `?node=` names another part.
    const [mapSelection, setMapSelection] = useMapSelection();
    const [revealNonce, setRevealNonce] = useState(0);

    const tunnels = raw?.tunnels ?? null;
    const attentionInput = useMemo(
        () => ({ overview, tunnels, events, policies, routing, haMembers: raw?.ha_members ?? [] }),
        [overview, tunnels, events, policies, routing, raw?.ha_members]
    );
    const attention = useMemo(() => needsAttention(attentionInput), [attentionInput]);
    const coverage = useMemo(() => readingCoverage(attentionInput), [attentionInput]);
    const verdict = useMemo(() => deviceVerdict(attention, attentionInput), [attention, attentionInput]);
    // Still reading: an empty list then means "not known yet", not "all clear".
    const attentionLoading = overview === null || [events, policies, routing].some((reading) => reading === undefined);
    /** The header's pill: select the node on the map and go up to it. */
    const inspectNode = useCallback(
        (node: AttentionNode) => {
            setMapSelection(node);
            setRevealNonce((n) => n + 1);
        },
        [setMapSelection]
    );
    /** A rule's name in an inspector: the whole table, scrolled to that rule's row. */
    const showRule = useCallback((ruleId: number) => {
        setPolicyFilter("all");
        setRuleFlash((prev) => ({ ruleId, nonce: (prev?.nonce ?? 0) + 1 }));
    }, []);
    /** The header's pill goes where its item's button would. */
    const openAttentionItem = useCallback(
        (item: AttentionItem) => {
            if (item.node) inspectNode(item.node);
            else scrollToId(ATTENTION_ANCHOR);
        },
        [inspectNode]
    );

    // The page's own shape while it loads, so nothing jumps when the data arrives (CTX-1396).
    if (loading) return <FwaasDetailsSkeleton />;
    if (error)
        return (
            <ErrorStateView
                title="Failed to load Virtual Firewall"
                message={error}
                onRetry={() => setRefresh((r) => r + 1)}
            />
        );
    if (!raw)
        return (
            <EmptyStateView
                title="Virtual Firewall not found"
                description="We couldn't find this Virtual Firewall. It may have been removed or you may not have access."
            />
        );

    const service = raw.details;

    return (
        <div className="flex flex-col gap-4">
            {/* Back navigation is provided by the SideNav (getBackRoute). */}
            <FwaasHeader
                service={service}
                vm={raw.vm}
                attention={attention}
                readAt={overview?.read_at ?? null}
                onAttentionClick={openAttentionItem}
            />

            <ServiceMapCard
                service={service}
                vm={raw.vm}
                haMembers={raw.ha_members}
                overview={overview}
                routing={routing}
                tunnels={tunnels}
                nat={nat}
                policies={policies}
                events={events}
                attention={attention}
                selection={mapSelection}
                onSelect={setMapSelection}
                revealNonce={revealNonce}
                onRuleClick={showRule}
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div id={ATTENTION_ANCHOR} className="scroll-mt-4">
                    <NeedsAttentionCard items={attention} loading={attentionLoading} coverage={coverage} />
                </div>
                <DeviceHealthCard
                    overview={overview}
                    vm={raw.vm}
                    routing={routing}
                    verdict={verdict}
                />
            </div>

            <div className="grid grid-cols-1 min-[1440px]:grid-cols-3 gap-4">
                <div className="min-[1440px]:col-span-2 min-w-0">
                    <PoliciesCard policies={policies} filter={policyFilter} onFilterChange={setPolicyFilter} flash={ruleFlash} />
                </div>
                <LicensesCard licenses={section("licenses")} />
            </div>
        </div>
    );
};

export default FwaasDetails;
